import { Component, OnInit, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  CreateAndUpdateOrderShareRequest,
  SharedRecipientDetails,
} from '@compumark/order-management-client';

import { SnackBarMessagePart } from '../../../services/snackbar/html-snack-bar.component';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { formatUtcToLocal } from '../../../util/date-util';
import { SharingService } from '../../services/sharing.service';

export interface SelectedRecipient {
  memberName?: string;
  memberCity?: string;
  memberId?: number;
  contactId?: number;
  contactName?: string;
  status?: 'PENDING' | 'SHARED';
  sharedByName?: string;
  sharedByContactId?: number;
  dateShared?: string;
}

@Component({
  selector: 'app-order-sharing-modal',
  templateUrl: './order-sharing-modal.component.html',
  styleUrls: ['./order-sharing-modal.component.scss'],
  standalone: false,
})
export class OrderSharingModalComponent implements OnInit {
  data = inject(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<OrderSharingModalComponent>>(MatDialogRef);
  private snackBar = inject(SnackbarService);
  private sharingService = inject(SharingService);

  protected selectedRecipients!: SelectedRecipient[];
  protected initialRecipients: SelectedRecipient[] = [];
  private initialRecipientMap = new Map<string, SelectedRecipient>();
  protected trademarkName!: any;
  protected isBulk = false;
  protected bulkOrderCount = 0;
  protected bulkOrders: any[] = [];

  ngOnInit(): void {
    this.isBulk = !!this.data?.isBulk;

    if (this.isBulk) {
      this.bulkOrderCount =
        this.data?.selectedNodes?.length ??
        this.data?.orderNumbers?.length ??
        0;
      this.bulkOrders = (this.data?.selectedNodes ?? []).map(
        (node: any) => node.data,
      );

      if (this.bulkOrderCount === 1 && this.bulkOrders.length === 1) {
        this.trademarkName = this.bulkOrders[0]?.trademarkName ?? '';
      }
    } else {
      this.trademarkName = this.data?.rowData?.trademarkName;
    }

    this.initialRecipients = this.mapToSelectedRecipients(
      this.data?.sharedRecipientDetails,
    );
    this.initialRecipients.forEach((r) => {
      this.initialRecipientMap.set(this.getKey(r.memberId, r.contactId), r);
    });
    this.selectedRecipients = [...this.initialRecipients];
  }

  onSelectionChange(selectedRecipients: SelectedRecipient[]): void {
    // Sort by memberName, then contactName
    this.selectedRecipients = selectedRecipients
      .map((recipient) => {
        const existing = this.initialRecipientMap.get(
          this.getKey(recipient.memberId, recipient.contactId),
        );

        if (existing) {
          return {
            ...recipient,
            status: 'SHARED' as const,
            sharedByName: existing.sharedByName,
            dateShared: existing.dateShared,
          };
        }
        return {
          ...recipient,
          status: 'PENDING' as const,
        };
      })
      .sort((a, b) => {
        const memberCompare = (a.memberName || '').localeCompare(
          b.memberName || '',
        );
        if (memberCompare !== 0) {
          return memberCompare;
        }
        return (a.contactName || '').localeCompare(b.contactName || '');
      });
  }

  onRemove(item: SelectedRecipient): void {
    this.selectedRecipients = this.selectedRecipients.filter(
      (recipient: SelectedRecipient) => {
        return !(
          recipient.memberId === item.memberId &&
          recipient.contactId === item.contactId
        );
      },
    );
  }

  onShareOrder(): void {
    this.dialogRef.close();
    this.sharingService
      .saveOrderShare(this.getCreateOrderShareRequest())
      .subscribe({
        next: (responseMap: Record<string, SharedRecipientDetails[]>) => {
          this.snackBar.openHtmlSnackBar(
            this.buildMessagePartsFromResponse(),
            'Dismiss',
          );
          if (this.isBulk) {
            this.data?.selectedNodes?.forEach((node: any) => {
              const orderNumber = node.data?.orderNumber;
              const sharedRecipientDetailsList = responseMap[orderNumber];
              if (sharedRecipientDetailsList) {
                node.setData({
                  ...node.data,
                  sharedRecipientDetailsList,
                });
              }
            });
          } else {
            const orderNumber = this.data?.rowData?.orderNumber;
            const sharedRecipientDetailsList = responseMap[orderNumber];
            this.data?.rowNode?.setData({
              ...this.data.rowData,
              sharedRecipientDetailsList,
            });
          }
        },
      });
  }

  private getKey(memberId?: number, contactId?: number): string {
    return `${memberId}-${contactId}`;
  }

  private getCreateOrderShareRequest(): CreateAndUpdateOrderShareRequest {
    const selected = this.selectedRecipients.map((recipient) => ({
      memberId: recipient.memberId,
      contactId: recipient.contactId,
    }));
    const unselected = this.initialRecipients
      .filter(
        (initial: SelectedRecipient) =>
          !this.selectedRecipients.some(
            (selectedRec: SelectedRecipient) =>
              selectedRec.memberId === initial.memberId &&
              selectedRec.contactId === initial.contactId,
          ),
      )
      .map((recipient) => ({
        memberId: recipient.memberId,
        contactId: recipient.contactId,
      }));

    const orderNumbers = this.isBulk
      ? this.data?.orderNumbers
      : [this.data?.rowData?.orderNumber];

    return {
      orderNumbers,
      selectedContacts: selected,
      unSelectedContacts: unselected,
    };
  }

  private mapToSelectedRecipients(
    detailsList: SharedRecipientDetails[],
  ): SelectedRecipient[] {
    const recipients =
      detailsList?.flatMap((details) =>
        (details.sharedTo ?? []).flatMap((member) =>
          (member.contacts ?? []).map((contact) => ({
            memberId: member.memberId,
            memberName: member.memberName,
            memberCity: member.memberCity,
            contactId: contact.contactId,
            contactName: contact.contactName,
            status: 'SHARED' as const,
            sharedByName: details.sharedBy?.contactName,
            sharedByContactId: details.sharedBy?.contactId,
            dateShared: formatUtcToLocal(details.dateShared),
          })),
        ),
      ) ?? [];
    // Sort by memberName, then contactName
    recipients.sort((a, b) => {
      const memberCompare = (a.memberName || '').localeCompare(
        b.memberName || '',
      );
      if (memberCompare !== 0) {
        return memberCompare;
      }
      return (a.contactName || '').localeCompare(b.contactName || '');
    });
    return recipients;
  }

  private buildMessagePartsFromResponse(): SnackBarMessagePart[] {
    if (this.isBulk && this.bulkOrderCount > 1) {
      return [
        { text: 'Sharing settings updated for ' },
        { text: `${this.bulkOrderCount}`, bold: true },
        { text: ' selected orders' },
      ];
    } else {
      return [
        { text: 'Sharing settings updated for ' },
        { text: this.trademarkName, bold: true },
      ];
    }
  }

  private hasChanges(): boolean {
    if (this.initialRecipients.length !== this.selectedRecipients.length) {
      return true;
    }

    const selectedKeys = new Set(
      this.selectedRecipients.map((r) => this.getKey(r.memberId, r.contactId)),
    );

    return this.initialRecipients.some(
      (r) => !selectedKeys.has(this.getKey(r.memberId, r.contactId)),
    );
  }

  isDisabled(): boolean {
    return !this.hasChanges();
  }
}
